<template>
  <section class="favourites" :class="{ desktop: $isDesktop }">
    <template v-if="favStore.currentlyLoggedIn">
      <button v-if="!$isDesktop" class="back-btn" @click="toggleAccountMenu">
        <img src="~/assets/images/down-rounded.svg" width="16" height="9" />
        <translate>User account</translate>
      </button>
      <div
        v-else-if="$isDesktop && !favStore.currentlyLoggedIn"
        class="back-wrapper"
      >
        <nuxt-link :to="$puLocalePath({ path: '/account/' })" class="back-btn">
          <img src="~/assets/images/down-rounded.svg" width="16" height="9" />
          <translate>User account</translate>
        </nuxt-link>
      </div>
      <translate v-else class="page-title">Favourites</translate>
    </template>
    <div
      class="favourites-lists"
      :class="{ 'logged-in': favStore.currentlyLoggedIn && !$isDesktop }"
    >
      <translate tag="h3"> Lists </translate>
      <div v-if="!favStore.currentlyLoggedIn" class="logged-out-note">
        <login-modal :modal-hash="'login-form-1'">
          <i18n-t
            tag="p"
            keypath="{logIn} to create and share custom lists and access your favourites from anywhere."
          >
            <template #logIn>
              <translate tag="a" @click.prevent> Log in </translate>
            </template>
          </i18n-t>
        </login-modal>
        <i18n-t
          tag="p"
          class="create-account"
          keypath="If you don't already have one you can {createAnAccountHere}."
        >
          <template #createAnAccountHere>
            <nuxt-link :to="$puLocalePath({ path: '/accounts/signup/' })">
              <translate>create an account here</translate>
            </nuxt-link>
          </template>
        </i18n-t>
      </div>

      <div class="lists">
        <pu-link
          v-if="!favStore.currentlyLoggedIn"
          name="favourites-my-favourites"
        >
          <wishlist-button
            :name="$t('My favourites')"
            :count="favStore.count"
            :image="favouritesImageUrl"
          />
        </pu-link>

        <!-- END DEFAULT LIST -->
        <!-- WISHLISTS -->
        <pu-link
          v-for="list in favStore.myWishlists"
          :key="list.id"
          :name="`favourites-listId-slug`"
          :params="{ listId: list.id, slug: slugify(list.name) }"
        >
          <wishlist-button
            :id="list.id"
            :name="list.name"
            :is-main="list.isMain"
            :status="list.status"
            :count="list.itemCount"
            :image="getWishlistImageUrl(list.primaryPhoto)"
          />
        </pu-link>

        <!-- END WISHLISTS -->

        <!-- ADD NEW WISHLIST -->
        <pu-modal
          v-if="favStore.currentlyLoggedIn"
          modal-hash="addNewWishlistForm"
          :use-portal="true"
          class="add-new-favourites-list-modal"
          :class="{ desktop: $isDesktop }"
          modal-class="add-new-favourites-list-modal"
        >
          <template #default="{ closeModal }">
            <wishlist-form-inputs
              :wishlist-name="state?.context?.newWishlistName"
              :errors="state?.context?.errors"
              @input="
                machine.send('CHANGE_NEW_WISHLIST_NAME', { value: $event })
              "
              @change="
                machine.send('CHANGE_NEW_WISHLIST_PRIVACY', { value: $event })
              "
              @saveToList="createNewWishlist(closeModal)"
            />
          </template>
          <template #modal-trigger>
            <button class="create-new-list">
              <img
                src="~/assets/images/plus-filled.svg"
                width="48"
                height="48"
                alt=""
              />
              <translate>Create a new list</translate>
            </button>
          </template>
        </pu-modal>
        <login-modal v-else :modal-hash="'login-form-2'">
          <button class="create-new-list loggedout" @click.prevent>
            <img
              src="~/assets/images/plus-filled.svg"
              width="48"
              height="48"
              alt=""
            />
            <translate>Create a new list (requires login)</translate>
          </button>
        </login-modal>
      </div>
      <!-- GUEST WISHLISTS -->
      <div v-if="guestWishlists.length" class="guest-lists">
        <translate tag="p"> Lists shared with me </translate>
        <div class="lists">
          <pu-link
            v-for="list in guestWishlists"
            :key="list.id"
            :name="`favourites-listId-slug`"
            :params="{ listId: list.id, slug: slugify(list.name) }"
          >
            <wishlist-button
              :id="list.id"
              :count="list.itemCount"
              :guest="true"
              :image="getWishlistImageUrl(list.primaryPhoto)"
              :name="list.name"
              :status="list.status"
              :wishlist="list"
            />
          </pu-link>
        </div>
      </div>

      <translate
        v-if="guestWishlists.length > 1 || favStore.myWishlists.length > 1"
        tag="p"
        class="footnote"
      >
        Sorted by number of campsites (most first)
      </translate>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { useLazyAsyncData } from 'nuxt/app'
import { computed, ref, onMounted } from 'vue'
import { interpret } from 'xstate-v4'
import { logger } from '~/utility/logger'
import { slugify } from '~/utility/slug'
import {
  FavouritesMachineInterpreter,
  getFavouritesMachine,
} from '../machines/favourites.machine'
import { WishlistPrimaryPhoto } from '../types'

const favStore = useFavouritesStore(usePinia())
const appMenuStore = useAppMenuStore(usePinia())

const guestWishlists = computed(() => {
  return favStore.guestWishlists.filter(
    (list) => list && list.listId && list.name,
  )
})

const { data: _ } = useLazyAsyncData('favourites', async () => {
  logger('FavouritesIndex.useLazyAsyncData.start')
  await favStore.initFavourites()
  return true
})

const state = ref<FavouritesMachineInterpreter['state'] | null>(null)
const machine = interpret(getFavouritesMachine(favStore)).onTransition((s) => {
  logger('FavouritesIndex.onTransition', s.toStrings())
  state.value = s
})

onMounted(async () => {
  logger('FavouritesIndex.onMounted', state.value)
  await favStore.getAllGuestWishlists()
  machine.start()
})

const favouritesImageUrl = computed(() =>
  favStore.favouritesPrimaryPhoto.url
    ? `/_cfi/cdn-cgi/image/f=auto,fit=cover,g=auto,quality=80,w=300,h=200${favStore.favouritesPrimaryPhoto.url}`
    : undefined,
)

function getWishlistImageUrl(image: WishlistPrimaryPhoto | null) {
  return image
    ? `/_cfi/cdn-cgi/image/f=auto,fit=cover,g=auto,quality=80,w=300,h=200${image.url}`
    : undefined
}

function toggleAccountMenu() {
  appMenuStore.toggleAccountMenu()
}

function createNewWishlist(closeModal: () => void) {
  machine.send('CREATE_NEW_WISHLIST', { callback: closeModal })
}
</script>

<style scoped>
.create-new-list {
  align-items: center;
  border: 1px solid var(--colour-page-background);
  box-shadow: 0 0.1em 0.75em var(--colour-page-background);
  display: flex;
  flex-direction: column;
  font-size: var(--body);
  gap: 1em;
  height: 100%;
  justify-content: center;
  margin: 0;
  max-width: 300px;
  min-height: 8em;
  overflow: hidden;
  padding: 0 1em;
  text-align: center;
  width: 100%;
}

.create-new-list img {
  filter: grayscale(1);
  opacity: 0.3;
}

.create-new-list:active img {
  filter: brightness(0.75);
  opacity: 1;
}

.favourites-lists {
  padding: 1em 1em 6em;
}

.favourites-lists.logged-in {
  background: var(--colour-page-background-light);
  min-height: 100vh;
}

.favourites-lists h3 {
  color: var(--colour-dark-blue);
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: 0.4em;
  padding: 0.5em 0.6em;
  border: none;
  box-shadow: none;
  font-size: var(--body);
  border-radius: 0;
}

.back-btn img {
  transform: rotate(90deg);
}

.back-btn:active {
  background: transparent;
  box-shadow: none;
  transform: none;
}

.lists {
  display: grid;
  gap: 1.4em;
  grid-auto-rows: auto;
  grid-template-columns: 1fr 1fr;
}

.lists :deep(a:nth-of-type(1n) button .img) {
  background-color: var(--colour-light-green);
}

.lists :deep(a:nth-of-type(2n) button .img) {
  background-color: var(--colour-form-blue);
}

.lists :deep(a:nth-of-type(3n) button .img) {
  background-color: var(--colour-offers);
}

.lists :deep(a:nth-of-type(4n) button .img) {
  background-color: var(--colour-orange);
}

.favourites {
  background-color: white;
  min-height: calc(100vh - 14vw);
  position: relative;
  width: 100vw;
  z-index: 10;
}

[data-menu-open='true'] .favourites {
  z-index: -1;
}

.intro {
  padding: 1em 1.4em 0.7em;
  align-items: flex-start;
}

.intro h3 {
  font-weight: normal;
}

.intro p {
  font-size: var(--body);
}

.count {
  font-weight: normal;
  color: var(--colour-text-light);
}

a {
  text-decoration: none;
}

.fetching-favourites {
  text-align: center;
  padding: 4em 1em 1em;
  min-height: 20vh;
}

.logged-out-note {
  padding: 0 0 1em;
}

.logged-out-note a {
  text-decoration: underline;
}

.guest-lists p,
.logged-out-note p {
  font-size: var(--body);
}

.guest-lists {
  margin-top: 1.5em;
}

.guest-lists p {
  margin: 0 0 0.5em 0;
  color: var(--colour-dark-blue);
  font-weight: bold;
}

.create-account {
  margin: 0.4em 0 0 0;
}

.lists :deep(.login-modal .modal-trigger-wrapper) {
  height: 100%;
}

.footnote {
  margin-top: 3em;
  font-size: var(--small);
  text-align: center;
  line-height: 1.1;
  color: var(--colour-text-light);
}

/* desktop & tablet */
.desktop.favourites {
  background-color: transparent;
  width: 100%;
}

.desktop.favourites header,
.desktop.favourites .favourites-lists {
  padding: 1.4em var(--page-width-padding);
}

.desktop.favourites .back-wrapper,
.desktop.favourites .favourites-lists {
  max-width: var(--page-width-max);
  margin: auto;
}

.desktop.favourites .back-wrapper {
  padding: 0.25em var(--page-width-padding) 0;
}

.desktop.favourites .back-btn {
  display: inline-flex;
  padding-left: 0;
}

.desktop.favourites .lists {
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 2em;
}

.desktop.favourites .guest-lists p,
.desktop.favourites .logged-out-note p {
  font-size: initial;
}

.desktop.favourites .guest-lists {
  margin-top: 2em;
}

.desktop.favourites .back-btn {
  font-size: 1.25em;
}

.desktop.favourites .create-new-list {
  transition: transform 0.25s ease-out;
  font-size: inherit;
}

.desktop.favourites .create-new-list:hover,
.desktop.favourites .create-new-list:focus-visible {
  box-shadow: 0 0 0 7px var(--focus-ring-colour);
  transform: scale(1.05);
  outline: none;
  border-color: var(--focus-ring-colour);
}

.desktop.favourites .footnote {
  font-size: initial;
}

.desktop.favourites .page-title {
  display: none;
}
</style>
<style>
.add-new-favourites-list-modal.modal-container {
  bottom: 0 !important;
  z-index: 100 !important;
}

.add-new-favourites-list-modal .modal-body {
  height: auto !important;
}

.add-new-favourites-list-modal .modal-footer {
  padding: 2em 1em;
}

.add-new-favourites-list-modal .button-container {
  align-items: center;
  display: flex;
  gap: 1em;
  justify-content: space-between;
  margin-top: 3em;
}

.add-new-favourites-list-modal .modal-trigger-wrapper {
  height: 100%;
}

/* desktop & tablet */
.desktop.favourites .add-new-favourites-list-modal.modal-container {
  bottom: auto !important;
}

.desktop.modal-container .action-btn.delete:hover,
.desktop.modal-container .button.sml:not(.primary):hover {
  background: #bdced7;
  border-color: #bdced7;
}

.desktop.modal-container .button.sml:not(.primary):hover {
  box-shadow: 0 0.3em #a0abb8;
}
</style>
